import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as R from 'ramda';

import * as inventoryAssetActions from '@atom/actions/inventoryAssetActions';
import { GET_ASSET } from '@atom/graph/asset';
import {
  GET_ASSET_REQUEST,
  UPDATE_ASSET_REQUEST,
} from '@atom/graph/assetRequest';
import client from '@atom/graph/client';
import { Modal, Progress } from '@atom/mui';
import {
  AssetRequest,
  AssetRequestStatus,
  AssetRequestType,
  AssetRequestUpdateInput,
} from '@atom/types/assetRequest';
import { AssetDetail } from '@atom/types/inventory';
import {
  Client,
  isCurrentClient,
} from '@atom/utilities/featureToggleUtilities';

import ARDOT118TransferView from './ardotView/ARDOT118TransferView';
import SDDOTAdjustmentView from './sddotView/SDDOTAdjustmentView';
import SDDOTInventoryView from './sddotView/SDDOTInventoryView';
import SDDOTReplenishmentView from './sddotView/SDDOTReplenishmentView';
import SDDOTStockpileView from './sddotView/SDDOTStockplieView';
import ConfirmationModal from './ConfirmationModal';
import ViewRequestContext from './ViewRequestContext';
import ViewRequestHeader from './ViewRequestHeader';

const styles = {
  modal: {
    padding: '1.5rem',
  },
};

interface Props {
  activeRequestId: string;
  setActiveRequestId: (id: string) => void;
  changeType?: (type: AssetRequestType) => void;
  assetDetailView?: boolean;
}

const ViewRequestModal = ({
  activeRequestId,
  setActiveRequestId,
  changeType = () => {},
  assetDetailView = false,
}: Props) => {
  const dispatch = useDispatch();

  const [activeRequest, setActiveRequest] = useState<AssetRequest>();
  const [fromAsset, setFromAsset] = useState<AssetDetail>();
  const [assetLoading, setAssetLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<AssetRequestStatus>();
  const [quantityApproved, setQuantityApproved] = useState<number>();
  const [comments, setComments] = useState<string>();
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const handleGetFromAsset = async (id: string) => {
    if (!id) {
      return;
    }

    setAssetLoading(true);
    const { data } = await client.query<{ asset: AssetDetail }, { id: string }>(
      {
        query: GET_ASSET,
        fetchPolicy: 'network-only',
        variables: {
          id,
        },
      },
    );

    setAssetLoading(false);
    setFromAsset(data?.asset);
  };

  const [getActiveRequest, { loading: activeRequestLoading }] = useLazyQuery<
    { assetRequest: AssetRequest },
    { id: string }
  >(GET_ASSET_REQUEST, {
    fetchPolicy: 'network-only',
    onCompleted: res => {
      const { type, assetId, fromAssetId } = res?.assetRequest;
      setActiveRequest(res.assetRequest);
      changeType(type);

      if (
        type === AssetRequestType.ARDOT_118_TRANSFER ||
        type === AssetRequestType.SDDOT_INVENTORY_TRANSFER
      ) {
        handleGetFromAsset(
          type === AssetRequestType.SDDOT_INVENTORY_TRANSFER
            ? fromAssetId
            : assetId,
        );
      }
    },
  });

  const handleGetActiveRequest = () => {
    getActiveRequest({
      variables: {
        id: activeRequestId,
      },
    });
  };

  const [updateAssetRequest] = useMutation<
    { assetRequestUpdate: AssetRequest },
    { input: AssetRequestUpdateInput }
  >(UPDATE_ASSET_REQUEST);

  useEffect(() => {
    if (!activeRequestId) {
      setStatus(null);
      setQuantityApproved(null);
      setComments(null);
      setActiveRequest(null);
      setFromAsset(null);
      setAssetLoading(false);
    }
  }, [activeRequestId]);

  useEffect(() => {
    if (!!activeRequestId) {
      handleGetActiveRequest();
    } else {
      setActiveRequest(null);
      setFromAsset(null);
    }
  }, [activeRequestId]);

  const updateRequest = async () => {
    setConfirmOpen(false);

    const requestId = activeRequestId;
    setActiveRequestId(null);

    await updateAssetRequest({
      variables: {
        input: {
          id: requestId,
          status,
          ...R.reject(R.isNil, {
            comments,
            quantityApproved,
          }),
        },
      },
    });

    setActiveRequestId(requestId);

    if (
      status === AssetRequestStatus.APPROVED &&
      assetDetailView &&
      isCurrentClient([Client.ARDOT])
    ) {
      dispatch(
        inventoryAssetActions.retrieveInventoryAssetDetail({
          id: activeRequest?.assetId,
        }),
      );
    }
  };

  const open = !!activeRequestId && !confirmOpen;

  const modalContent = {
    [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: <SDDOTInventoryView />,
    [AssetRequestType.SDDOT_STOCKPILE_TRANSFER]: <SDDOTStockpileView />,
    [AssetRequestType.SDDOT_REPLENISHMENT]: <SDDOTReplenishmentView />,
    [AssetRequestType.SDDOT_ADJUSTMENT]: <SDDOTAdjustmentView />,
    [AssetRequestType.ARDOT_118_TRANSFER]: <ARDOT118TransferView />,
  };

  const modalWidth =
    activeRequest?.type === AssetRequestType.SDDOT_REPLENISHMENT ? 'xl' : 'sm';

  const loading = assetLoading || activeRequestLoading;

  return (
    <ViewRequestContext.Provider
      value={{
        fromAsset,
        activeRequest,
        handleGetActiveRequest,
        setActiveRequestId,
        status,
        setStatus,
        quantityApproved,
        setQuantityApproved,
        comments,
        setComments,
        confirmOpen,
        setConfirmOpen,
      }}
    >
      <>
        <Modal
          title={<ViewRequestHeader />}
          open={open}
          onCancel={() => setActiveRequestId(null)}
          data-cy="viewAssetRequest"
          contentStyle={styles.modal}
          width={modalWidth}
          disableFooter
        >
          {loading ? <Progress /> : modalContent[activeRequest?.type]}
        </Modal>
        <ConfirmationModal updateRequest={updateRequest} />
      </>
    </ViewRequestContext.Provider>
  );
};

export default ViewRequestModal;
